import { ChangeDetectorRef, Component, NgZone, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { Option } from '../shared/models/sr/sr-committee-search-data';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { IOption } from '../../controls/models/option';

@Component({
    selector: 'app-ballot-create-dates',
    templateUrl: './ballot-create-dates.component.html',
    styleUrls: ['./ballot-create-dates.component.scss'],
    standalone: false
})
export class BallotCreateDatesComponent implements OnInit, OnDestroy {
  @Output() selectedDatesChange: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  minStartDate = new Date();
  maxStartDate: Date | null = null;
  minEndDate: Date = new Date(this.minStartDate);
  errorMessage: string = "";
  selectDateForm: FormGroup;
  durationOptions: IOption<string>[] = [
    { value: '2', label: '2 weeks' },
    { value: '4', label: '4 weeks' },
    { value: '6', label: '6 weeks' },
    { value: '8', label: '8 weeks' },
    { value: 'Custom', label: 'Custom' },
  ];
  private unsubscribe$ = new Subject<void>();

  constructor(private changeDetectorRef: ChangeDetectorRef, private zone: NgZone, private fb: FormBuilder) {
    this.selectDateForm = this.fb.group({
      startDateCtrl: new FormControl(),
      durationCtrl: new FormControl('Custom'),
      endDateCtrl: new FormControl(),
    });
  }

  ngOnInit(): void {
    this.initializeDateChange();
    this.detectEndDateChanges();
  }


  initializeDateChange(): void {
    this.selectDateForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr): boolean => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((formData) => {
        this.handleValueChanges(formData);
        this.selectedDatesChange.emit(this.selectDateForm);
      });
  }

  handleValueChanges(formData: any): void {
    const today = new Date();
    const minStartDate = new Date(today.setDate(today.getDate() + 1));
    const startDate = formData.startDateCtrl;
    const duration = formData.durationCtrl;
    const endDateControl = this.selectDateForm.get('endDateCtrl');
    this.zone.run(() => {
      if (!startDate) {
        this.errorMessage = "Please select start date."
        return;
      }
      else {
        this.errorMessage = "";
      }
      const selectedStartDate = new Date(startDate);
      if (duration && duration !== 'Custom') {
        const weeks = parseInt(duration);
        const days = weeks * 7;
        selectedStartDate.setDate(selectedStartDate.getDate() + days);
        this.maxStartDate = new Date(selectedStartDate);
        this.maxStartDate.setDate(this.maxStartDate.getDate() - 1);

        endDateControl?.patchValue(selectedStartDate, { emitEvent: false });
        endDateControl?.disable({ emitEvent: false });
      } else {
        endDateControl?.enable({ emitEvent: false });
        selectedStartDate.setDate(selectedStartDate.getDate() + 1);
        this.minEndDate = new Date(selectedStartDate);
      }

      this.selectDateForm.markAllAsTouched();

      this.changeDetectorRef.detectChanges();
    });

    Promise.resolve().then(() => {
      const updatedFormData = {
        ...this.selectDateForm.getRawValue(),
        endDateCtrl: this.selectDateForm.getRawValue().endDateCtrl
      };

      const updatedFormGroup = new FormGroup({
        startDateCtrl: new FormControl(updatedFormData.startDateCtrl),
        durationCtrl: new FormControl(updatedFormData.durationCtrl),
        endDateCtrl: new FormControl(updatedFormData.endDateCtrl)
      });

      this.selectedDatesChange.emit(updatedFormGroup);
      this.changeDetectorRef.detectChanges();
    });
  }

  detectEndDateChanges(): void {
    this.selectDateForm.get('endDateCtrl')?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedEndDate) => {
        if (!this.maxStartDate) {
          this.maxStartDate = new Date();
        }
        this.maxStartDate = new Date(selectedEndDate);
        this.maxStartDate.setDate(this.maxStartDate.getDate() - 1);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
